/**
 * Application version information
 *
 * Version numbering uses a shared sequential build number (git tags).
 * Format: 1.0.<build_number>[-staging]
 *
 * Both staging and production increment from the same counter,
 * so you can compare versions directly:
 *   staging v1.0.55 vs prod v1.0.52 = staging is 3 deploys ahead
 *
 * These values are injected during build time by GitHub Actions
 * or default to development values when running locally.
 */

export const APP_VERSION = process.env.REACT_APP_VERSION || 'dev';
export const BUILD_DATE = process.env.REACT_APP_BUILD_DATE || new Date().toISOString();
export const COMMIT_SHA = process.env.REACT_APP_COMMIT_SHA || 'local';
export const BUILD_NUMBER = process.env.REACT_APP_BUILD_NUMBER || '0';

/**
 * Get formatted version string
 */
export const getVersionString = (): string => {
  if (APP_VERSION === 'dev') {
    // In development, show commit SHA if available
    if (COMMIT_SHA !== 'local') {
      return `Build ${COMMIT_SHA.substring(0, 7)}`;
    }
    // Local development - show "Build local" instead of vague "dev"
    return 'Build local';
  }
  // CI passes the git TAG, which already carries the `v` (`v1.0.75`), so
  // prefixing unconditionally rendered **`vv1.0.75`** in the footer
  // ([[BUGS #173]]). Strip a leading `v` before adding our own, so both a
  // tag (`v1.0.75`) and a bare version (`1.0.75`) come out as `v1.0.75`.
  //
  // The tests did not catch this because every fixture passed the bare form —
  // a shape CI never sends. [[BUGS #109]]'s lesson, in miniature: a fixture is
  // only evidence if it came from the thing it stands in for.
  return `v${APP_VERSION.replace(/^v/i, '')}`;
};

/**
 * Get formatted build date
 */
export const getBuildDate = (): string => {
  try {
    const date = new Date(BUILD_DATE);
    // Check if date is valid (Invalid Date returns NaN for getTime())
    if (isNaN(date.getTime())) {
      return 'Unknown';
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return 'Unknown';
  }
};

/**
 * Get full version info object
 */
export const getVersionInfo = () => ({
  version: APP_VERSION,
  buildDate: BUILD_DATE,
  commitSha: COMMIT_SHA,
  buildNumber: BUILD_NUMBER,
  versionString: getVersionString(),
  buildDateFormatted: getBuildDate()
});
